import { queryOptions, type SolidMutationOptions, useMutation } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { TeamInvitationApi } from "~/solidJs/pages/[TeamInvitationPage]/data";
import { getPublicMessageFromError } from "~/solidJs/shared/helpers/errorHelpers";
import { showToastMessage, ToastMessageTypes } from "~/solidJs/widgets/WorkspaceToast";
import { ApiErrorHandlers, BaseApi } from "./baseApi";

// Enums
export enum InvitationStatus {
	Accepted = "accepted",
	Pending = "pending"
}

export enum MemberRole {
	Owner = "owner",
	Member = "member"
}

export enum TeamCreationStatus {
	Success = "success",
	Pending = "pending"
}

// Types
export interface CreditsBreakdown {
	id: string; // UUID for breakdown
	credits_left: number;
	credits_received: number;
	credit_type_name: string;
	expiration_date: string | null;
}

// Account credits breakdown (from /team/accounts_credits endpoint)
export interface AccountCreditsItem {
	company_account_id: number | null; // null for owner pool
	user_name: string | null;
	total_credits: number;
}

export interface AccountsCreditsResponse {
	accounts: AccountCreditsItem[];
}

export interface TariffLimit {
	node_id: number;
	id_global: string;
	limits_per_month: number | null;
	count_tasks: number | null;
}

export interface TeamMember {
	company_account_id: number;
	tariff_name: string;
	user_id: string | null;
	user_name: string | null;
	credits_balance: number;
	credits_break_down: CreditsBreakdown[];
	invitation_status: InvitationStatus | null;
	invitation_token: string | null;
	invitation_id: number | null;
	role: MemberRole | null;
	expiration_date: string | null;
	will_be_deleted: string | null;
	is_infinity: boolean;
}

// Lightweight team credits response (from /team/credits_info)
export interface TeamCreditsInfoResponse {
	members: TeamMemberCreditsInfo[];
}

export interface TeamMemberCreditsInfo {
	company_account_id: number;
	user_id: string | null;
	user_name: string | null;
	credits_balance: number;
	credits_break_down: CreditsBreakdown[];
	is_infinity: boolean;
}

// Next payment information
export interface NextPayment {
	payment_date: string;
	total_amount: number;
	currency: string;
	payment_system: string;
	subscription_type?: "monthly" | "annually";
}

export interface TeamInfoResponse {
	tariff_name: string;
	team_name: string;
	company_status: "active" | "inactive";
	team_member_amount: number;
	team_member_total_amount: number;
	your_role: MemberRole;
	members?: TeamMember[] | null;
	payment_id?: string | null;
	start_time?: string | null;
	end_time?: string | null;
	next_payment?: NextPayment | null;
	team_subscription_status: "inactive" | "active" | "cancelled" | "due";
	plan_status?: "old" | "new";
	waiting_payments_url: string[];
	cancel_at?: string | null;
}

export enum DurationMonths {
	Monthly = 1,
	SixMonths = 6,
	Yearly = 12
}

export interface CreateTeamRequest {
	team_name: string;
	seats: number;
	payment_method_id?: string | null;
	payment_currency?: string | null;
	plan?: string;
	duration_months?: DurationMonths;
}

export interface CreateTeamResponse {
	team_id: number;
	redirect_url?: string | null;
	status: TeamCreationStatus;
}

export interface ReactivateTeamRequest {
	seats: number;
	payment_method_id?: string | null;
	payment_system: "stripe" | "yookassa" | "manual" | "appsumo";
	duration_type: "monthly" | "annually";
	seats_subscriptions?: Array<{
		plan_name: string;
		quantity: number;
	}> | null;
}

export interface ReactivateTeamResponse {
	team_id: number;
	payment_id: string | null;
	redirect_url: string | null;
	status: "success" | "pending";
}

export interface TeamPricingRequest {
	seats: number;
	payment_method_id?: string | null;
	currency?: string | null;
	plan?: string;
	duration_months?: DurationMonths;
}

export interface TeamPricingResponse {
	price: string;
	discount_price?: string | null;
	price_per_seat: string;
	next_billing_date: string;
}

export interface CalculateSeatsRemovalRequest {
	seats_to_remove: string[];
}

export interface CalculateSeatsRemovalResponse {
	current_seats: number;
	new_seats: number;
	current_price_per_seat: number;
	new_price_per_seat: number;
	current_total_price: number;
	new_total_price: number;
	removed_members: string[];
	removed_unused_seats: number;
	change_apply_date: string;
}

export interface AddMembersRequest {
	new_seats_subscriptions: Array<{
		plan_name: string;
		quantity: number;
	}>;
	payment_method_id?: string | null;
}

export interface AddMembersResponse {
	redirect_url?: string | null;
	status: string;
}

export interface CalculateAddMembersCostRequest {
	new_seats_subscriptions: Array<{
		plan_name: string;
		quantity: number;
	}>;
}

export interface NewMemberCost {
	plan_name: string;
	quantity: number;
	cost_per_seat: number;
	total_cost: number;
}

export interface CalculateAddMembersCostResponse {
	total_cost: number;
	prorated_cost: number;
	currency: string;
	payment_system: string;
	new_members_costs: NewMemberCost[];
	immediate_charge: boolean;
	next_billing_date?: string;
	next_billing_price?: number;
	subscription_type?: "monthly" | "annually";
}

export interface CreateInvitationRequest {
	company_account_id: number;
	email: string;
}

export interface CreateInvitationResponse {
	token: string;
}

export interface CancelInvitationRequest {
	invitation_id: string;
}

export interface RemoveMemberRequest {
	company_account_id: number;
}

export interface RetryPaymentResponse {
	redirect_url: string;
	payment_id: string;
	status: string;
}

export enum SubscriptionAction {
	Upgrade = "upgrade",
	Downgrade = "downgrade",
	Delete = "delete"
}

export interface SubscriptionUpgradeAction {
	company_account_id: number;
	action: SubscriptionAction;
	new_plan?: string;
}

export interface ManageCompanySubscriptionRequest {
	upgrades: SubscriptionUpgradeAction[];
	when: "immediately" | string;
}

export interface ManageCompanySubscriptionResponse {
	status: string;
	scheduled_upgrades: number;
	scheduled_time: string;
}

// Buy credits for team
export enum BuyCreditsMode {
	ForMember = "for_member",
	ForOwner = "for_owner"
}

export interface BuyTeamCreditsRequest {
	product_api_label: string;
	mode: BuyCreditsMode;
	company_account_id?: number;
	payment_method_id?: string;
	payment_system?: string;
}

export interface BuyTeamCreditsResponse {
	status: string;
	redirect_url?: string | null;
	payment_id?: number;
	message?: string;
}

// Modify recurrent payment
export interface ModifyRecurrentRequest {
	is_disable_recurrent: boolean;
}

export interface ModifyRecurrentResponse {
	status: string;
	message: string;
	is_disable_recurrent: boolean;
}

// Calculate upgrade cost
export enum UpgradeAction {
	Upgrade = "upgrade",
	Downgrade = "downgrade",
	Delete = "delete"
}

export enum UpgradeWhen {
	Immediately = "immediately",
	FromNextMonth = "from_next_month",
	FromNewBillingPeriod = "from_new_billing_period"
}

export interface UpgradeItem {
	company_account_id: number;
	action: UpgradeAction;
	new_plan: string | null;
}

export interface CalculateUpgradeCostRequest {
	upgrades: UpgradeItem[];
	when: UpgradeWhen;
}

export interface MemberChange {
	company_account_id: number;
	action: string;
	current_plan: string;
	new_plan: string | null;
	cost_change: number;
}

export interface CalculateUpgradeCostResponse {
	total_cost_change: number;
	current_monthly_cost: number;
	new_monthly_cost: number;
	current_monthly_cost_per_seat: number;
	new_monthly_cost_per_seat: number;
	members_changes: MemberChange[];
	total_cost: number;
	subscription_type?: "monthly" | "annually";
}

// Credit distribution types

export interface TransferCreditsRequest {
	source_company_account_id: number | null; // null for owner pool
	target_company_account_id: number;
	amount: number;
}

export interface TransferCreditsResponse {
	status: string;
	message: string;
	remaining_credits: number;
}

// Preview credits distribution types
export interface PreviewDistributionItem {
	target_company_account_id: number;
	amount: number;
}

export interface PreviewCreditsDistributionRequest {
	source_company_account_id: number | null; // null for owner pool
	distributions: PreviewDistributionItem[];
}

export interface PreviewMemberBalance {
	company_account_id: number;
	user_name: string | null;
	current_balance: number;
	transfer_amount: number;
	projected_balance: number;
}

export interface PreviewCreditsDistributionResponse {
	source_breakdown_id: string;
	source_credits_left: number;
	total_to_distribute: number;
	source_projected_balance: number;
	members: PreviewMemberBalance[];
}

// Transfer Pool feature availability types
export interface TransferPoolFeatureDetails {
	enabled_since: string;
	plan_name: string;
}

export interface TransferPoolAvailabilityResponse {
	is_available: boolean;
	feature_details: TransferPoolFeatureDetails | null;
}

export class TeamApi extends BaseApi {
	static baseUrl = "api/v2/team";

	static getTeamInfoOptions() {
		// Get userId directly without createMemo to ensure consistent queryKey
		const userId = getUserStore()?.id;
		return queryOptions({
			queryKey: ["teamInfo", userId],
			queryFn: () => TeamApi.getTeamInfo(),
			refetchInterval: 5000,
			refetchOnWindowFocus: true,
			staleTime: 5000,
			gcTime: 15000
			// Note: removed custom reconcile - TanStack Query handles updates correctly
			// The previous reconcile was causing issues with member updates not reflecting in UI
		});
	}

	static async getTeamInfo(): Promise<TeamInfoResponse | null> {
		const { data, status } = await axiosClient.axiosClient.get<TeamInfoResponse | null>(
			`/${TeamApi.baseUrl}/info`
		);
		if (status === 204) {
			return null;
		}
		return data;
	}

	/**
	 * Get lightweight credits information for team members
	 * This is a new endpoint that returns only credits data without full team info
	 */
	static getTeamCreditsInfoOptions() {
		const userId = getUserStore()?.id;
		return queryOptions({
			queryKey: ["teamCreditsInfo", userId],
			queryFn: () => TeamApi.getTeamCreditsInfo(),
			refetchInterval: 5000,
			refetchOnWindowFocus: true,
			staleTime: 5000,
			gcTime: 15000
		});
	}

	static async getTeamCreditsInfo(): Promise<TeamCreditsInfoResponse> {
		const { data } = await axiosClient.axiosClient.get<TeamCreditsInfoResponse>(
			`/${TeamApi.baseUrl}/credits_info`
		);
		return data;
	}

	static createTeamOptions() {
		return queryOptions({
			queryKey: ["createTeam"],
			queryFn: () => {
				throw new Error("createTeam must be called with mutation");
			},
			enabled: false
		});
	}

	static async createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<CreateTeamResponse>(
				`/${TeamApi.baseUrl}/create`,
				request,
				{
					retryMax: 0
				}
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async reactivateTeam(request: ReactivateTeamRequest): Promise<ReactivateTeamResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<ReactivateTeamResponse>(
				`/${TeamApi.baseUrl}/reactivate`,
				request,
				{
					retryMax: 0
				}
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static async addMembers(request: AddMembersRequest): Promise<AddMembersResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<AddMembersResponse>(
				`/${TeamApi.baseUrl}/add_members`,
				request,
				{
					retryMax: 0
				}
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	// Query options for calculating add members cost
	static calculateAddMembersCostOptions(request: CalculateAddMembersCostRequest) {
		return queryOptions({
			queryKey: ["calculateAddMembersCost", request],
			queryFn: () => TeamApi.calculateAddMembersCost(request),
			enabled: false // Disabled by default, enable when needed
		});
	}

	static async calculateAddMembersCost(
		request: CalculateAddMembersCostRequest
	): Promise<CalculateAddMembersCostResponse> {
		const { data } = await axiosClient.axiosClient.post<CalculateAddMembersCostResponse>(
			`/${TeamApi.baseUrl}/calculate_add_members_cost`,
			request
		);
		return data;
	}
	static useSaveTelegramMutation(
		options?: SolidMutationOptions<void, unknown, { username: string }>
	) {
		return useMutation<void, unknown, { username: string }>(() => ({
			mutationKey: ["saveUserTelegram"],
			mutationFn: (data) => TeamApi.saveUserTelegram(data),
			...options
		}));
	}

	static async saveUserTelegram(request: { username: string }) {
		try {
			const { data } = await axiosClient.axiosClient.post(
				"api/v2/front/save_telegram_username",
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useCreateInvitationMutation(
		options?: SolidMutationOptions<CreateInvitationResponse, unknown, CreateInvitationRequest>
	) {
		return useMutation<CreateInvitationResponse, unknown, CreateInvitationRequest>(() => ({
			mutationKey: ["createInvitation"],
			mutationFn: (data) => TeamApi.createInvitation(data),
			...options
		}));
	}

	static async createInvitation(
		request: CreateInvitationRequest
	): Promise<CreateInvitationResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<CreateInvitationResponse>(
				`/${TeamApi.baseUrl}/create_invitation`,
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useCancelInvitationMutation(
		options?: SolidMutationOptions<void, unknown, CancelInvitationRequest>
	) {
		return useMutation<void, unknown, CancelInvitationRequest>(() => ({
			mutationKey: ["cancelInvitation"],
			mutationFn: (data) => TeamApi.cancelInvitation(data),
			...options
		}));
	}

	static async cancelInvitation(request: CancelInvitationRequest): Promise<void> {
		try {
			await axiosClient.axiosClient.delete(`/${TeamApi.baseUrl}/cancel_invitation`, {
				data: request
			});
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useRemoveMemberMutation(
		options?: SolidMutationOptions<void, unknown, RemoveMemberRequest>
	) {
		return useMutation<void, unknown, RemoveMemberRequest>(() => ({
			mutationKey: ["removeMember"],
			mutationFn: (data) => TeamApi.removeMember(data),
			...options
		}));
	}

	static async removeMember(request: RemoveMemberRequest): Promise<void> {
		try {
			await axiosClient.axiosClient.post(`/${TeamApi.baseUrl}/remove_member`, request);
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static calculateTeamPricingOptions(request: TeamPricingRequest) {
		return queryOptions({
			queryKey: ["calculateTeamPricing", request],
			queryFn: () => TeamApi.calculateTeamPricing(request)
		});
	}

	static async calculateTeamPricing(request: TeamPricingRequest): Promise<TeamPricingResponse> {
		const { data } = await axiosClient.axiosClient.post<TeamPricingResponse>(
			"/api/v2/payments/calculate_team_pricing",
			request
		);
		return data;
	}

	static calculateSeatsRemovalOptions(seatsToRemove: string[]) {
		return queryOptions({
			queryKey: ["calculateSeatsRemoval", seatsToRemove],
			queryFn: () => TeamApi.calculateSeatsRemoval({ seats_to_remove: seatsToRemove }),
			enabled: seatsToRemove.length > 0,
			placeholderData(prop) {
				return prop;
			}
		});
	}

	static async calculateSeatsRemoval(
		request: CalculateSeatsRemovalRequest
	): Promise<CalculateSeatsRemovalResponse> {
		const { data } = await axiosClient.axiosClient.post<CalculateSeatsRemovalResponse>(
			`/${TeamApi.baseUrl}/calculate-seats-removal`,
			request
		);
		return data;
	}

	static async activateInvitation(token: string) {
		try {
			const res = await axiosClient.axiosClient.post<{
				status: string;
				message: string;
				company_id: 0;
				company_account_id: 0;
			}>("/api/v2/front/activate_invitation", { token });
			return res.data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	/**
	 * Register team invitation if token exists in localStorage
	 * Returns true if invitation was successfully activated, false otherwise
	 */
	static async registerTeamInvitation(): Promise<boolean> {
		const token = TeamInvitationApi.getTeamInvitationToken();
		if (!token) {
			return false;
		}
		try {
			const status = await TeamApi.activateInvitation(token);
			showToastMessage({
				title:
					status.status === "success"
						? "Team invitation activated"
						: "Failed to activate team invitation",
				description: status.message,
				type:
					status.status === "success"
						? ToastMessageTypes.Success
						: ToastMessageTypes.Error
			});
			TeamInvitationApi.removeTeamInvitationToken();
			return true;
		} catch (e) {
			if (!(e instanceof Error)) {
				TeamInvitationApi.removeTeamInvitationToken();
			}
			showToastMessage({
				title: "Failed to activate team invitation",
				type: ToastMessageTypes.Error,
				description: getPublicMessageFromError(e) || undefined
			});
			return false;
		}
	}

	static useDeleteTeamMutation(options?: SolidMutationOptions<void, unknown, void>) {
		return useMutation<void, unknown, void>(() => ({
			mutationKey: ["deleteTeam"],
			mutationFn: () => TeamApi.deleteTeam(),
			...options
		}));
	}

	static async deleteTeam(): Promise<void> {
		try {
			await axiosClient.axiosClient.delete(`/${TeamApi.baseUrl}/delete`);
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useRetryPaymentMutation(
		options?: SolidMutationOptions<RetryPaymentResponse, unknown, void>
	) {
		return useMutation<RetryPaymentResponse, unknown, void>(() => ({
			mutationKey: ["retryPayment"],
			mutationFn: () => TeamApi.retryPayment(),
			...options
		}));
	}

	static async retryPayment(): Promise<RetryPaymentResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<RetryPaymentResponse>(
				`/${TeamApi.baseUrl}/retry_payment`
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useManageSubscriptionMutation(
		options?: SolidMutationOptions<
			ManageCompanySubscriptionResponse,
			unknown,
			ManageCompanySubscriptionRequest
		>
	) {
		return useMutation<
			ManageCompanySubscriptionResponse,
			unknown,
			ManageCompanySubscriptionRequest
		>(() => ({
			mutationKey: ["manageSubscription"],
			mutationFn: (data) => TeamApi.manageSubscription(data),
			...options
		}));
	}

	static async manageSubscription(
		request: ManageCompanySubscriptionRequest
	): Promise<ManageCompanySubscriptionResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<ManageCompanySubscriptionResponse>(
				`/${TeamApi.baseUrl}/manage_subscription`,
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useBuyTeamCreditsMutation(
		options?: SolidMutationOptions<BuyTeamCreditsResponse, unknown, BuyTeamCreditsRequest>
	) {
		return useMutation<BuyTeamCreditsResponse, unknown, BuyTeamCreditsRequest>(() => ({
			mutationKey: ["buyTeamCredits"],
			mutationFn: (data) => TeamApi.buyTeamCredits(data),
			...options
		}));
	}

	static async buyTeamCredits(request: BuyTeamCreditsRequest): Promise<BuyTeamCreditsResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<BuyTeamCreditsResponse>(
				`/${TeamApi.baseUrl}/buy_credits`,
				request,
				{
					retryMax: 0
				}
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static useModifyRecurrentMutation(
		options?: SolidMutationOptions<ModifyRecurrentResponse, unknown, ModifyRecurrentRequest>
	) {
		return useMutation<ModifyRecurrentResponse, unknown, ModifyRecurrentRequest>(() => ({
			mutationKey: ["modifyRecurrent"],
			mutationFn: (data) => TeamApi.modifyRecurrent(data),
			...options
		}));
	}

	static async modifyRecurrent(
		request: ModifyRecurrentRequest
	): Promise<ModifyRecurrentResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<ModifyRecurrentResponse>(
				`/${TeamApi.baseUrl}/modify_recurrent`,
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	static calculateUpgradeCostOptions(upgrades: UpgradeItem[], when: UpgradeWhen) {
		return queryOptions({
			queryKey: ["calculateUpgradeCost", upgrades, when],
			queryFn: () => TeamApi.calculateUpgradeCost({ upgrades, when }),
			enabled: upgrades.length > 0,
			placeholderData(prop) {
				return prop;
			}
		});
	}

	static async calculateUpgradeCost(
		request: CalculateUpgradeCostRequest
	): Promise<CalculateUpgradeCostResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<CalculateUpgradeCostResponse>(
				`/${TeamApi.baseUrl}/calculate_upgrade_cost`,
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}

	// Get tariff limits for all company accounts (owner only)
	static getCompanyAccountTariffLimitsOptions(companyAccountId: number) {
		return queryOptions({
			queryKey: ["companyAccountTariffLimits", companyAccountId],
			queryFn: () => TeamApi.getCompanyAccountTariffLimits(companyAccountId)
		});
	}

	static async getCompanyAccountTariffLimits(companyAccountId: number): Promise<TariffLimit[]> {
		const { data } = await axiosClient.axiosClient.get<TariffLimit[]>(
			`/${TeamApi.baseUrl}/company_account/${companyAccountId}/tariff_limits`
		);
		return data;
	}

	// Get tariff limits for current team member
	static getMyTariffLimitsOptions() {
		return queryOptions({
			queryKey: ["myTariffLimits"],
			queryFn: () => TeamApi.getMyTariffLimits()
		});
	}

	static async getMyTariffLimits(): Promise<TariffLimit[]> {
		const { data } = await axiosClient.axiosClient.get<TariffLimit[]>(
			`/${TeamApi.baseUrl}/my_tariff_limits`
		);
		return data;
	}

	// Credit distribution methods
	static getAccountsCreditsOptions() {
		return queryOptions({
			queryKey: ["accountsCredits"],
			queryFn: () => TeamApi.getAccountsCredits()
		});
	}

	static async getAccountsCredits(): Promise<AccountsCreditsResponse> {
		const { data } = await axiosClient.axiosClient.get<AccountsCreditsResponse>(
			`/${TeamApi.baseUrl}/accounts_credits`
		);
		return data;
	}

	static previewCreditsDistributionOptions(request: PreviewCreditsDistributionRequest) {
		return queryOptions({
			queryKey: ["previewCreditsDistribution", request],
			queryFn: () => TeamApi.previewCreditsDistribution(request),
			enabled: false
		});
	}

	static async previewCreditsDistribution(
		request: PreviewCreditsDistributionRequest
	): Promise<PreviewCreditsDistributionResponse> {
		const { data } = await axiosClient.axiosClient.post<PreviewCreditsDistributionResponse>(
			`/${TeamApi.baseUrl}/preview_credits_distribution`,
			request
		);
		return data;
	}

	// Transfer Pool feature availability
	static getTransferPoolAvailabilityOptions() {
		return queryOptions({
			queryKey: ["transferPoolAvailability"],
			queryFn: () => TeamApi.getTransferPoolAvailability()
		});
	}

	static async getTransferPoolAvailability(): Promise<TransferPoolAvailabilityResponse> {
		const { data } = await axiosClient.axiosClient.get<TransferPoolAvailabilityResponse>(
			`/${TeamApi.baseUrl}/features/transfer_pool`
		);
		return data;
	}

	static useTransferCreditsMutation(
		options?: SolidMutationOptions<TransferCreditsResponse, unknown, TransferCreditsRequest>
	) {
		return useMutation<TransferCreditsResponse, unknown, TransferCreditsRequest>(() => ({
			mutationKey: ["transferCredits"],
			mutationFn: (data) => TeamApi.transferCredits(data),
			...options
		}));
	}

	static async transferCredits(
		request: TransferCreditsRequest
	): Promise<TransferCreditsResponse> {
		try {
			const { data } = await axiosClient.axiosClient.post<TransferCreditsResponse>(
				`/${TeamApi.baseUrl}/transfer_credits`,
				request
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}
}
